/**
 * Leg speed plausibility rules, mirroring PigRunManager (EN-1495 review round
 * 2, R2-7) so the tracksheet marks the verified legs the backend ignores.
 * Keep in sync with PigRunAlgorithmHelper (MaximumPlausibleSpeedFactor,
 * MinimumPlausibleLegSeconds, MinimumLegDistanceMeters, EvaluateLeg) and
 * PigRunSpeedDefaults.DefaultExpectedSpeedMetersPerSecond. Drift is caught by
 * the shared cases e2e/fixtures/pig-run-leg-speed-cases.json, which the API
 * tests also run (identical copy in ProPipe.WebApi.Tests, R3-7 option B).
 *
 * Only the per-leg rule is mirrored. The backend's choice of which leg gives
 * the current speed (skipping a suspect passage, falling back to an earlier
 * leg, and the 2 h MaximumFallbackLegAgeSeconds limit on that fallback) is
 * not: the tracksheet shows each leg between passed markers on its own row and
 * never picks a current speed. The current speed it shows comes from
 * PigRunManager over SignalR, already after those rules. So the age rule has
 * nothing to apply to here. Interim until PigRunManager sends the speed basis
 * and per-leg rejections itself (R3-7 option A, follow-up).
 */

/** 4 mph in m/s, the backend default when a run's expected speed is invalid. */
export const DEFAULT_EXPECTED_SPEED_METERS_PER_SECOND = 1.78816;

/** A leg faster than this times max(expected, default) speed is ignored. */
export const MAXIMUM_PLAUSIBLE_SPEED_FACTOR = 5;

/** A leg shorter than this (including zero or negative times) is ignored. */
export const MINIMUM_PLAUSIBLE_LEG_SECONDS = 10;

/** A leg shorter than this distance gives no usable speed. */
export const MINIMUM_LEG_DISTANCE_METERS = 1;

export type LegSpeedRejection =
  | 'too-short-distance'
  | 'too-short-duration'
  | 'too-fast';

export interface LegSpeedEvaluation {
  /**
   * distance / time when both are positive and finite (shown even when the
   * leg is rejected, so the bad value is visible), otherwise null.
   */
  speedMetersPerSecond: number | null;
  /** Why the backend ignores this leg for the current speed, or null. */
  rejection: LegSpeedRejection | null;
}

/** The run's expected speed, or the 4 mph default when it is not valid. */
export function resolveExpectedSpeed(
  expectedSpeed: number | null | undefined
): number {
  return typeof expectedSpeed === 'number' &&
    Number.isFinite(expectedSpeed) &&
    expectedSpeed > 0
    ? expectedSpeed
    : DEFAULT_EXPECTED_SPEED_METERS_PER_SECOND;
}

/** The fastest plausible leg speed in m/s for a run's expected speed. */
export function maximumPlausibleSpeed(
  expectedSpeed: number | null | undefined
): number {
  return (
    MAXIMUM_PLAUSIBLE_SPEED_FACTOR *
    Math.max(
      resolveExpectedSpeed(expectedSpeed),
      DEFAULT_EXPECTED_SPEED_METERS_PER_SECOND
    )
  );
}

export function evaluateLegSpeed(
  distanceMeters: number,
  seconds: number,
  expectedSpeed: number | null | undefined
): LegSpeedEvaluation {
  const speedMetersPerSecond =
    Number.isFinite(distanceMeters) &&
    Number.isFinite(seconds) &&
    distanceMeters > 0 &&
    seconds > 0
      ? distanceMeters / seconds
      : null;

  let rejection: LegSpeedRejection | null = null;
  if (
    !Number.isFinite(distanceMeters) ||
    distanceMeters < MINIMUM_LEG_DISTANCE_METERS
  ) {
    rejection = 'too-short-distance';
  } else if (
    !Number.isFinite(seconds) ||
    seconds < MINIMUM_PLAUSIBLE_LEG_SECONDS
  ) {
    rejection = 'too-short-duration';
  } else if (
    speedMetersPerSecond === null ||
    speedMetersPerSecond > maximumPlausibleSpeed(expectedSpeed)
  ) {
    rejection = 'too-fast';
  }

  return { speedMetersPerSecond, rejection };
}

/** Short text for a rejected leg, for the tracksheet and CSV. */
export function describeLegSpeedRejection(
  rejection: LegSpeedRejection | null
): string {
  switch (rejection) {
    case 'too-short-distance':
      return `Ignored: shorter than ${MINIMUM_LEG_DISTANCE_METERS} m`;
    case 'too-short-duration':
      return `Ignored: under ${MINIMUM_PLAUSIBLE_LEG_SECONDS} s or out of order`;
    case 'too-fast':
      return `Ignored: more than ${MAXIMUM_PLAUSIBLE_SPEED_FACTOR}x max(expected, 4 mph)`;
    default:
      return '';
  }
}
